// apps/patient/src/pages/messages/MessageCompose.tsx

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft,
    Search,
    User,
    AlertCircle,
    X,
    Info,
    Stethoscope,
    UserCircle
} from 'lucide-react';
import { patientApiService as apiService } from '../../services/api';
import { usePatient } from '../../contexts/PatientContext';
import MessageComposer from '../../components/messages/MessageComposer';
import { cn } from '../../lib/utils';

export default function MessageCompose() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { patient } = usePatient();

    // Pre-populated from URL params
    const preselectedProviderId = searchParams.get('provider_id');

    // State
    const [careTeam, setCareTeam] = useState([]);
    const [filteredCareTeam, setFilteredCareTeam] = useState([]);
    const [selectedRecipient, setSelectedRecipient] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showRecipientList, setShowRecipientList] = useState(false);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    // Form state
    const [subject, setSubject] = useState('');
    const [category, setCategory] = useState('general');

    // Load care team members
    useEffect(() => {
        fetchCareTeam();
    }, []);

    // Pre-select provider if ID provided
    useEffect(() => {
        if (preselectedProviderId && careTeam.length > 0) {
            const provider = careTeam.find(p => p.id === preselectedProviderId);
            if (provider) {
                setSelectedRecipient(provider);
            }
        }
    }, [preselectedProviderId, careTeam]);

    const fetchCareTeam = async () => {
        setLoading(true);
        try {
            const response = await apiService.getCareTeam();
            setCareTeam(response || []);
            setFilteredCareTeam(response || []);
        } catch (err) {
            console.error('Error fetching care team:', err);
            setError('Failed to load care team members');
        } finally {
            setLoading(false);
        }
    };

    // Search care team members
    useEffect(() => {
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            const filtered = careTeam.filter(member => {
                const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
                const role = member.role?.toLowerCase() || '';
                const specialty = member.specialty?.toLowerCase() || '';

                return fullName.includes(query) ||
                    role.includes(query) ||
                    specialty.includes(query);
            });
            setFilteredCareTeam(filtered);
        } else {
            setFilteredCareTeam(careTeam);
        }
    }, [searchQuery, careTeam]);

    const handleRecipientSelect = (recipient) => {
        setSelectedRecipient(recipient);
        setShowRecipientList(false);
        setSearchQuery('');
    };

    const handleSendMessage = async (messageData) => {
        if (!selectedRecipient || !subject.trim()) {
            setError('Please select a recipient and enter a subject');
            return;
        }

        setSending(true);
        setError(null);

        try {
            let participants =[
                {
                    "role": "patient",
                    "user_id": patient.id,
                    "user_name": `${patient.first_name} ${patient.last_name}`,
                    "notification_preference": "all"
                },
                {
                    "role": 'provider',
                    "user_id": selectedRecipient.id,
                    "user_name": `${selectedRecipient.first_name} ${selectedRecipient.last_name}`,
                    "notification_preference": "all"
                }
            ]
            // Create thread with patient-initiated settings
            const threadData = {
                subject: subject.trim(),
                category,
                priority: 'medium', // Patients can't set priority
                recipient_id: selectedRecipient.id,
                initial_message: messageData.body,
                participants: participants,
                patient_id: patient.patient_id,
                patient_name: `${patient.first_name} ${patient.last_name}`,
                last_sender_name: `${patient.first_name} ${patient.last_name}`,
                last_sender_id: patient.id,
                last_message_preview: messageData.body.slice(0, 100),
                status: 'open'
            };

            const threadResponse = await apiService.createMessageThread(threadData);

            // Send the first message
            const firstMessage = {
                body: messageData.body,
                is_urgent: false, // Patients can't mark as urgent
                sender_id: patient.id,
                sender_name: `${patient.first_name} ${patient.last_name}`,
                sender_type: 'patient'
            };

            const messageResponse = await apiService.sendMessage(threadResponse.id, firstMessage);

            // Handle attachments if any
            if (messageData.attachments?.length > 0) {
                messageResponse.threadId = threadResponse.id;
            }

            // Navigate to the new thread
            setTimeout(() => {
                navigate(`/messages?thread=${threadResponse.id}`);
            }, messageData.attachments?.length > 0 ? 1000 : 100);

            return messageResponse;
        } catch (err) {
            console.error('Error creating message:', err);
            setError('Failed to send message. Please try again.');
            setSending(false);
            throw err;
        }
    };

    // Patient-appropriate categories
    const categoryOptions = [
        { value: 'general', label: 'General Question' },
        { value: 'clinical', label: 'Medical Question' },
        { value: 'prescription', label: 'Prescription' },
        { value: 'appointment', label: 'Appointment' },
        { value: 'billing', label: 'Billing' },
        { value: 'administrative', label: 'Administrative' }
    ];

    const getRoleIcon = (role) => {
        if (role === 'provider' || role === 'doctor') return Stethoscope;
        return UserCircle;
    };

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Header */}
            <div className="border-b px-4 sm:px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/messages')}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                        <h1 className="text-xl font-semibold text-gray-900">New Message</h1>
                    </div>

                    <button
                        onClick={() => navigate('/messages')}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="px-4 sm:px-6 py-3 bg-red-50 border-b border-red-200">
                    <div className="flex items-center gap-2 text-red-800">
                        <AlertCircle className="h-4 w-4" />
                        <span className="text-sm">{error}</span>
                        <button
                            onClick={() => setError(null)}
                            className="ml-auto text-red-600 hover:text-red-800"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Form */}
            <div className="flex-1 overflow-y-auto">
                <div className="px-4 sm:px-6 py-6 space-y-6">
                    {/* Recipient Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            To <span className="text-red-500">*</span>
                        </label>

                        {selectedRecipient ? (
                            <div className="flex items-center justify-between p-3 border border-gray-300 rounded-lg bg-gray-50">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        {getRoleIcon(selectedRecipient.role) === Stethoscope ? (
                                            <Stethoscope className="h-5 w-5 text-primary" />
                                        ) : (
                                            <UserCircle className="h-5 w-5 text-primary" />
                                        )}
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900">
                                            {selectedRecipient.first_name} {selectedRecipient.last_name}
                                        </p>
                                        {/* Removing until we have implemented more granular roles
                                        <p className="text-sm text-gray-600">
                                            {selectedRecipient.role || 'Provider'}
                                            {selectedRecipient.specialty && ` • ${selectedRecipient.specialty}`}
                                        </p>
                                        */}
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedRecipient(null)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        ) : (
                            <div className="relative">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onFocus={() => setShowRecipientList(true)}
                                        placeholder="Search for a provider or care team member..."
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                </div>

                                {/* Care team dropdown */}
                                {showRecipientList && (
                                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                        {loading ? (
                                            <div className="p-4 text-center text-gray-500">
                                                Loading care team...
                                            </div>
                                        ) : filteredCareTeam.length === 0 ? (
                                            <div className="p-4 text-center text-gray-500">
                                                No care team members found
                                            </div>
                                        ) : (
                                            filteredCareTeam.map(member => {
                                                const RoleIcon = getRoleIcon(member.role);
                                                return (
                                                    <button
                                                        key={member.id}
                                                        onClick={() => handleRecipientSelect(member)}
                                                        className="w-full px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-left"
                                                    >
                                                        <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                                                            <RoleIcon className="h-5 w-5 text-gray-600" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="font-medium text-gray-900">
                                                                {member.first_name} {member.last_name}
                                                            </p>
                                                            {/* Removing until we have implemented more granular roles
                                                            <p className="text-sm text-gray-600">
                                                                {member.role || 'Provider'}
                                                                {member.specialty && ` • ${member.specialty}`}
                                                            </p> 
                                                            */}
                                                        </div>
                                                    </button>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Subject */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Subject <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="What is this message about?"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category
                        </label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                            {categoryOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex gap-3">
                            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800">
                                <p className="font-medium mb-1">Message Tips:</p>
                                <ul className="list-disc list-inside space-y-1 text-blue-700">
                                    <li>Be specific about your question or concern</li>
                                    <li>Include relevant details like symptoms or medication names</li>
                                    <li>For urgent medical issues, call 911 or visit the emergency room</li>
                                    <li>Allow 1-2 business days for a response</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Message Composer */}
            <div className="border-t">
                <MessageComposer
                    threadId={null}
                    onSend={handleSendMessage}
                    onCancel={() => { }} // Add this line - empty function since we don't need cancel for new messages
                    disabled={!selectedRecipient || !subject.trim() || sending}
                    placeholder="Type your message..."
                    isPatient={true}
                />
            </div>
        </div>
    );
}